'use client'
import * as React from 'react'
import dynamic from 'next/dynamic'
import { algoliaAnalytics, AlgoliaEventName } from 'algolia/algolia'
import { useTranslations } from 'next-intl'
import { isLoggedInQuerySelector } from 'auth/selectors'
import { useUserQuery } from 'auth/hooks'
import { SpinnerIcon } from 'primitives/icons'
import { tw } from 'utils/classnames'
import type { AlgoliaSearchedProductsLocalStorage } from 'algolia/algolia'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { setCookie } from 'utils/dom'
import { googleTrackProductDownloadClick } from '@repo/analytics/google-site-tag'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import * as Sentry from '@sentry/nextjs'
import { usePaymentsDialog } from 'payments/components/payments-dialog-wrapper-context'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'
import { useIsStripeBillingEnabled } from '@repo/web-ui/hooks/use-site-data'
import { useStartFreeTrial } from 'payments/start-free-trial-button/hooks/use-start-free-trial'
import {
  useUserIsEligibleForAllAccessFreeTrialQuery,
  useUserIsEligibleForAllAccessYearlyUpgradeQuery,
} from '@repo/cf-user/hooks/use-user-data'
import { localStore } from 'utils/storage'

import { useContentIncreaseDownloadCountMutation } from '@web/gen/content'
import { DownloadIcon } from '../icons/download'
import {
  useIsFirstDownload,
  useIsFirstLegacyProductDownload,
  useProductDataQuery,
} from '@web/hooks/use-product-data'
import { getIsProductFree } from '../content-api/config'
import { ALL_ACCESS_PRODUCT_REFERRAL_COOKIE_NAME } from '@web/utils/env'
import {
  useNeedsPaymentState,
  useSelectedAssetState,
  useWasDownloadedState,
} from '@web/app/[locale]/(web)/prod/[slug]/product-asset-context'

import { downloadAsset, getAssetUrl, getIsMobileSafari, prefetchSuggestions } from './helpers'

import { useSuggestionsLimit } from './preview/suggestions/suggestions'
import { useLegacyProductQuery } from '@web/gen/cf'
import { useProductId } from '@web/app/[locale]/(web)/prod/[slug]/product-id-context'
import { useDownloadLegacyProduct } from '@repo/web-ui/hooks/use-download-legacy-product'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'

interface BuyButtonProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode
  disabled?: boolean
  icon?: React.ReactNode
  isLegacy?: boolean
  isPriceFree?: boolean
  isSticky?: boolean
}

const TryForFreeModal = dynamic(
  () => import('./try-for-free-modal').then(module => module.TryForFreeModal),
  {
    ssr: false,
  },
)

// oxlint-disable-next-line complexity
export function ProductBuyButton({
  icon,
  className,
  children,
  isLegacy = false,
  isPriceFree = false,
  isSticky = false,
  ...rest
}: BuyButtonProps) {
  const { isPending: isLoadingUserData, data: userData } = useUserQuery(undefined, {
    select: data => data?.me?.user,
  })
  const isLoggedIn =
    !isLoadingUserData &&
    isLoggedInQuerySelector({
      me: { user: userData },
    })
  const productId = useProductId()
  const {
    data: productData,
    isPending: isLoadingProduct,
    refetch: refetchProductData,
  } = useProductDataQuery(!isLegacy)

  const { data: productLegacyDataResponse, isPending: isLoadingLegacyProduct } =
    useLegacyProductQuery(
      {
        productId: Number(productId),
      },
      {
        enabled: Boolean(isLegacy),
      },
    )
  const productLegacyData = productLegacyDataResponse?.legacyProductsFetchProduct
  const {
    isFirstDownload: isLegacyFirstDownload,
    refetch: refetchLegacyLicenses,
    isLoading: isLoadingLegacyIsFirstDownload,
  } = useIsFirstLegacyProductDownload(Number(productId ?? 0), Boolean(isLegacy) && isLoggedIn)
  const {
    isFirstDownload,
    refetch: refetchLicenses,
    isLoading: isLoadingIsFirstDownload,
  } = useIsFirstDownload(productData?.id !== undefined && isLoggedIn && !isLegacy)

  const t = useTranslations('prod.product_description_page.buy')
  const isStripeBillingEnabled = useIsStripeBillingEnabled()
  const [selectedAsset] = useSelectedAssetState()
  const [, setPaymentUrl] = useNeedsPaymentState()
  const [, setShowSuggestion] = useWasDownloadedState()
  const [wasDownloaded, setWasDownloaded] = React.useState(false)
  const userId = userData?.id
  const isAllAccess =
    !isLoadingUserData && Boolean(userData?.subscriptionAccess.allAccess.isSubscribed)
  const { data: isEligibleForAllAccessFreeTrialData } =
    useUserIsEligibleForAllAccessFreeTrialQuery()
  const { data: isEligibleForAllAccessYearlyUpgradeData } =
    useUserIsEligibleForAllAccessYearlyUpgradeQuery()
  const isEligibleForAllAccessFreeTrial = isLoggedIn && isEligibleForAllAccessFreeTrialData
  const isEligibleForAllAccessYearlyUpgrade = isLoggedIn && isEligibleForAllAccessYearlyUpgradeData
  const contentIncreaseDownloadCountMutation = useContentIncreaseDownloadCountMutation({
    retry: 3,
    retryDelay: attempt => attempt * 1000,
  })
  const [isTryForFreeModalOpen, setIsTryForFreeModalOpen] = React.useState(false)
  const { open: openPayments } = usePaymentsDialog()
  const { startFreeTrial, isPending } = useStartFreeTrial()
  const { openModal: openAuthModal } = useUserAuthModal()
  const isFreeProduct = isPriceFree || getIsProductFree(productData?.category.root?.id)
  const isAllAccessOrFree = isLoggedIn && (isAllAccess || isFreeProduct)
  const [isLoadingAssets, setIsDownloadingAssets] = React.useState(false)
  const suggestionsLimit = useSuggestionsLimit()
  const { isLoading: isDownloadingLegacyAssets, download: downloadLegacyProduct } =
    useDownloadLegacyProduct()

  React.useEffect(() => {
    let timeoutRef: NodeJS.Timeout | null = null
    async function triggerFileDownload() {
      try {
        const { href, download, payment } = await getAssetUrl(
          selectedAsset.assetId,
          selectedAsset?.assetPath,
        )
        if (getIsMobileSafari()) {
          /**
           * Safari has some issues with clicking on download links. It will open a confirmation dialog to download the file.
           * While this dialog is open the requests happening on the main page, will become a background task which will not be
           * forwarding all the cookies. This causes GQL requets to fail and new loaded images to appear as broken.
           */
          await prefetchSuggestions(suggestionsLimit)
        }
        // oxlint-disable-next-line no-negated-condition
        if (typeof payment !== 'boolean') {
          setPaymentUrl(payment.paymentUrl)
        } else {
          downloadAsset(href, download)
        }
      } catch (error) {
        Sentry.captureException(error)
        return
      } finally {
        setIsDownloadingAssets(false)
        if (isLoggedIn && isFreeProduct && !isAllAccess) {
          setIsTryForFreeModalOpen(true)
        }
      }
      await contentIncreaseDownloadCountMutation.mutateAsync(
        {
          productId,
        },
        {
          onError(e) {
            Sentry.captureException(
              new Error(
                `Error while increasing download count for product ${productId}:
                  error = ${(e as Error)?.message ? (e as Error)?.message : (e as string)}`,
              ),
            )
          },
        },
      )

      refetchLicenses()
      setWasDownloaded(false)
    }

    async function triggerLegacyFileDownload() {
      try {
        await downloadLegacyProduct({
          productId: productId,
          isFree: isFreeProduct,
          onSuccess: () => {
            refetchLegacyLicenses()
          },
        })
      } catch (error) {
        Sentry.captureException(error)
        return
      } finally {
        setWasDownloaded(false)
        if (isLoggedIn && isFreeProduct && !isAllAccess) {
          setIsTryForFreeModalOpen(true)
        }
      }
    }

    if (wasDownloaded) {
      timeoutRef = setTimeout(isLegacy ? triggerLegacyFileDownload : triggerFileDownload, 300)
    }

    return () => {
      timeoutRef && clearTimeout(timeoutRef)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasDownloaded, selectedAsset, productId, isLegacy, isFreeProduct])

  // oxlint-disable-next-line complexity
  const handleDownloadClick = async (e?: React.MouseEvent) => {
    // Add cookie to track referral product
    try {
      setCookie({
        name: ALL_ACCESS_PRODUCT_REFERRAL_COOKIE_NAME || 'all_access_referral_product',
        value: productId,
        domain: '.creativefabrica.com',
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }

    const algoliaSearchedProducts: Partial<AlgoliaSearchedProductsLocalStorage> = JSON.parse(
      localStore.getString(`algoliaSearchedProducts.${userId || 'guest'}`) || '{}',
    )
    // Tracking event for Amplitude
    sendAmplitudeEvent(AmplitudeEvents.DOWNLOADED_PRODUCT, {
      category: productData?.category.root?.name || '',
      subcategory: productData?.category ? [productData.category.name] : [],
      image_url: selectedAsset?.assetPath || '',
      name: productData?.title || '',
      product_id: productId,
      productId,
      type: 'subscription',
      isLegacy,
      download_origin: 'product_page',
    })

    googleTrackProductDownloadClick(
      `${isLegacy ? productLegacyData?.productMainCategory?.title : productData?.category?.name} - Regular`,
    )

    savePaymentModalEntryPoint(
      isSticky ? PAYMENT_ENTRY_POINTS.PDP_TOP_BANNER : PAYMENT_ENTRY_POINTS.PDP_DOWNLOAD_SIDEBAR,
    )

    let productWasSearched = false
    let queryId = ''
    let index = ''

    if (algoliaSearchedProducts) {
      const items = algoliaSearchedProducts.items || {}
      Object.keys(items).forEach(key => {
        // oxlint-disable-next-line unicorn/prefer-includes
        if (Object.keys(items[key]).indexOf(productId) !== -1) {
          index = key
          productWasSearched = true
          queryId = items[key][productId]
        }
      })
    }

    // Tracking event for Algolia
    if (productWasSearched) {
      algoliaAnalytics('convertedObjectIDsAfterSearch', {
        index: index,
        eventName: AlgoliaEventName.PRODUCT_DOWNLOADED,
        objectIDs: [productId],
        queryID: queryId,
      })
    } else {
      algoliaAnalytics('convertedObjectIDs', {
        index: process.env.NEXT_PUBLIC_CONTENT_ALGOLIA_INDEX ?? '',
        eventName: AlgoliaEventName.PRODUCT_DOWNLOADED,
        objectIDs: [productId],
      })
    }

    const hasHrefAttribute = e && Boolean((e.currentTarget as HTMLAnchorElement).href)

    if (!hasHrefAttribute && !isAllAccess && !isFreeProduct && isEligibleForAllAccessFreeTrial) {
      await startFreeTrial()
      return
    }

    if (
      !hasHrefAttribute &&
      !isAllAccess &&
      isEligibleForAllAccessYearlyUpgrade &&
      !isFreeProduct
    ) {
      openPayments()
      return
    }

    if (isAllAccessOrFree) {
      e?.preventDefault()
      setIsDownloadingAssets(true)
      setShowSuggestion(true)
      setWasDownloaded(true)
    }
    if (!isLegacy) {
      refetchProductData()
    }
  }

  const renderSubmitButton = React.useCallback(
    (
      text: React.ReactNode,
      onClick: React.MouseEventHandler<HTMLButtonElement> &
        React.MouseEventHandler<HTMLAnchorElement>,
      href?: string,
    ) => {
      const Component = href ? LinkButton : Button
      return (
        <Component
          id="download-button"
          icon={icon ?? <DownloadIcon className="size-5 shrink-0" />}
          text={children ?? text}
          onClick={onClick}
          href={href}
          className={className}
          {...rest}
        />
      )
    },
    [icon, children, className, rest],
  )

  if (
    isPending ||
    isLoadingUserData ||
    (isLegacy ? isDownloadingLegacyAssets : isLoadingAssets) ||
    (isLegacy ? isLoadingLegacyIsFirstDownload : isLoadingIsFirstDownload) ||
    (isLegacy ? isLoadingLegacyProduct : isLoadingProduct)
  ) {
    return (
      <Button
        id="download-button"
        key="loading"
        disabled
        className={className}
        icon={<SpinnerIcon data-testid="spinner" className="size-5 w-40 animate-spin" />}
        {...rest}
      />
    )
  }

  if (!isLoggedIn && isFreeProduct) {
    return renderSubmitButton(
      t.rich('cta_free', {
        span: text => <span className="whitespace-nowrap">{text}</span>,
      }),
      () => {
        openAuthModal()
      },
    )
  }

  if (isAllAccessOrFree) {
    return (
      <>
        {renderSubmitButton(
          isFirstDownload || isLegacyFirstDownload ? t('cta') : t('cta_again'),
          handleDownloadClick,
        )}
        <TryForFreeModal
          isOpen={isTryForFreeModalOpen}
          onClose={() => setIsTryForFreeModalOpen(false)}
        />
      </>
    )
  }

  if (isLoggedIn && !isAllAccess) {
    const text = isEligibleForAllAccessFreeTrial
      ? t.rich('cta_free', {
          span: text => <span className="whitespace-nowrap">{text}</span>,
        })
      : t.rich('cta_no_trial', {
          span: text => <span className="whitespace-nowrap">{text}</span>,
        })

    return renderSubmitButton(text, handleDownloadClick)
  }

  return renderSubmitButton(
    t.rich('cta_free', {
      span: text => <span className="whitespace-nowrap">{text}</span>,
    }),
    handleDownloadClick,
    isStripeBillingEnabled ? AppRoutes.B_FREE_TRIAL : AppRoutes.FREE_TRIAL,
  )
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode
  text?: React.ReactNode
}

export function Button({ icon, text, className, ...rest }: ButtonProps) {
  return (
    // oxlint-disable-next-line react/button-has-type
    <button
      {...rest}
      className={tw(
        'hover:bg-secondary-700 text-service-white text-14 mt-2 flex h-[45px] w-min items-center justify-center rounded bg-[#0F7B59] px-3 text-center font-bold uppercase leading-4 transition-[background-color] disabled:cursor-not-allowed disabled:opacity-50 min-[600px]:w-fit min-[600px]:whitespace-nowrap',
        className,
      )}
    >
      {icon}
      <span className="px-2">{text}</span>
    </button>
  )
}

Button.displayName = 'Button'

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  icon?: React.ReactNode
  text?: React.ReactNode
  prefetch?: boolean
  component?: 'Link' | 'a'
}

export function LinkButton({
  icon,
  text,
  className,
  prefetch = false,
  component = 'a',
  ...rest
}: LinkProps) {
  return (
    <Link
      className={tw(
        'hover:bg-secondary-700 text-service-white text-14 mt-2 flex h-[45px] w-min items-center justify-center rounded bg-[#0F7B59] px-3 text-center font-bold uppercase leading-4 transition-[background-color] min-[600px]:w-fit min-[600px]:whitespace-nowrap',
        className,
      )}
      component={component}
      prefetch={prefetch}
      {...rest}
    >
      {icon}
      <span className="px-2">{text}</span>
    </Link>
  )
}

Button.displayName = 'LinkButton'
